import React, { useCallback, useEffect, useState } from "react";
import { CalendarDays, CircleDot, DollarSign, Hash, RefreshCw, TrendingDown, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import API from "../services/api";

const marketColors = [
  "linear-gradient(90deg, #2563eb, #4f46e5)",
  "linear-gradient(90deg, #9333ea, #c026d3)",
  "linear-gradient(90deg, #16a34a, #22c55e)",
  "linear-gradient(90deg, #ea580c, #f97316)",
  "linear-gradient(90deg, #db2777, #ec4899)",
  "linear-gradient(90deg, #0891b2, #06b6d4)",
  "linear-gradient(90deg, #dc2626, #ef4444)",
  "linear-gradient(90deg, #4f46e5, #6366f1)",
];

function AdminStakeAnalysis({ marketType = "normal" }) {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(getIndiaDateValue());
  const [data, setData] = useState({ summary: {}, markets: [] });
  const isStarline = marketType === "starline";
  const marketRows = Array.isArray(data.markets)
    ? [...data.markets].sort((a, b) => parseMarketTime(a.open_time) - parseMarketTime(b.open_time) || String(a.name || "").localeCompare(String(b.name || "")))
    : [];

  const loadSummary = useCallback(async () => {
    try {
      const res = await API.get(`/admin/stake-analysis?date=${selectedDate}&market_type=${marketType}`);
      setData(res.data || { summary: {}, markets: [] });
    } catch {
      setData({ summary: {}, markets: [] });
    }
  }, [marketType, selectedDate]);

  const deleteMarket = async (marketId, marketName) => {
    if (isStarline) return;
    if (!window.confirm(`Delete market "${marketName}"?`)) return;
    try {
      await API.delete(`/admin/markets/${marketId}`);
      await loadSummary();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to delete market");
    }
  };

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  return (
    <AdminLayout>
      <section style={page}>
        <div style={pageHeader}>
          <div>
            <h1 style={title}>{isStarline ? "Starline Stake Analysis" : "Stake Analysis"}</h1>
            <p style={subtitle}>{isStarline ? "Starline market betting analytics" : "Real-time betting analytics by normal market"}</p>
          </div>

          <div style={headerActions}>
            <div style={dateWrap}>
              <CalendarDays size={18} color="#475569" />
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={dateInput} />
            </div>
            <button type="button" style={refreshButton} onClick={loadSummary}>
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>

        <section style={summaryCard}>
          <div style={summaryGrid} className="admin-mobile-three">
            <SummaryBox label="TOTAL STAKE" value={formatCompactMoney(data.summary.total_stake)} tone="#eff6ff" color="#2563eb" />
            <SummaryBox label="TOTAL BETS" value={formatCount(data.summary.total_bets)} tone="#f0fdf4" color="#16a34a" />
            <SummaryBox label="MARKETS" value={formatCount(data.summary.total_markets)} tone="#faf5ff" color="#9333ea" />
            <SummaryBox label="OPEN STAKE" value={formatCompactMoney(data.summary.open_stake)} tone="#ecfeff" color="#0891b2" />
            <SummaryBox label="CLOSE STAKE" value={formatCompactMoney(data.summary.close_stake)} tone="#fff1f2" color="#e11d48" />
          </div>
        </section>

        {!marketRows.length ? (
          <section style={emptyState}>
            <strong>No stake data for {selectedDate}</strong>
            <span>Choose another date or click refresh after new bets are placed.</span>
          </section>
        ) : (
        <div style={marketGrid}>
          {marketRows.map((market, index) => (
            <button
              key={market.id}
              type="button"
              onClick={() => navigate(`/admin/stake-analysis/${market.id}?date=${selectedDate}`)}
              style={marketCard}
            >
              <div style={{ ...marketHeader, background: marketColors[index % marketColors.length] }}>
                <div style={marketTitleRow}>
                  <span style={marketInitial}>{getInitials(market.name)}</span>
                  <strong>{market.name}</strong>
                </div>
                <div style={marketTotal}>
                  <span>Total</span>
                  <strong>{formatCompactMoney(market.total_stake)}</strong>
                </div>
              </div>
              {!isStarline && (
                <div style={marketActionBar}>
                  <button
                    type="button"
                    style={deleteButton}
                    onClick={(event) => {
                      event.stopPropagation();
                      deleteMarket(market.id, market.name);
                    }}
                  >
                    Delete Market
                  </button>
                </div>
              )}

              <div style={sessionPanelOpen}>
                <div>
                  <strong>OPEN</strong>
                  <div style={sessionMeta}>Stake</div>
                  <div style={sessionValueGreen}>{formatCompactMoney(market.open_stake)}</div>
                </div>
                <div style={betsMeta}>
                  <span>Bets</span>
                  <strong>{market.open_bets || 0}</strong>
                </div>
              </div>

              <div style={sessionPanelClose}>
                <div>
                  <strong>CLOSE</strong>
                  <div style={sessionMeta}>Stake</div>
                  <div style={sessionValueRed}>{formatCompactMoney(market.close_stake)}</div>
                </div>
                <div style={betsMeta}>
                  <span>Bets</span>
                  <strong>{market.close_bets || 0}</strong>
                </div>
              </div>

              <div style={cardFoot}>
                <span style={cardFootText}>{`${market.total_bets || 0} total bets`}</span>
                <span style={cardFootLink}>Open Details</span>
              </div>
            </button>
          ))}
        </div>
        )}
      </section>
    </AdminLayout>
  );
}

function SummaryBox({ label, value, tone, color }) {
  const Icon = summaryIcons[label] || DollarSign;

  return (
    <article style={{ ...summaryBox, background: tone }}>
      <Icon size={22} color={color} strokeWidth={2.4} />
      <strong style={summaryValue}>{value}</strong>
      <span style={summaryLabel}>{label}</span>
    </article>
  );
}

const summaryIcons = {
  "TOTAL STAKE": DollarSign,
  "TOTAL BETS": Hash,
  MARKETS: CircleDot,
  "OPEN STAKE": TrendingUp,
  "CLOSE STAKE": TrendingDown
};

function formatCompactMoney(value) {
  const amount = Number(value || 0);
  if (amount >= 1000) return `Rs ${(amount / 1000).toFixed(1)}K`;
  return `Rs ${amount}`;
}

function formatCount(value) {
  return new Intl.NumberFormat("en-IN").format(Number(value || 0));
}

function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((item) => item[0])
    .join("")
    .toUpperCase() || "MK";
}

function parseMarketTime(value) {
  const raw = String(value || "").trim().toLowerCase();
  const meridiem = raw.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/);
  if (meridiem) {
    let hour = Number(meridiem[1]);
    const minute = Number(meridiem[2]);
    if (meridiem[3] === "pm" && hour !== 12) hour += 12;
    if (meridiem[3] === "am" && hour === 12) hour = 0;
    return hour * 60 + minute;
  }
  const plain = raw.match(/^(\d{1,2}):(\d{2})/);
  if (plain) return Number(plain[1]) * 60 + Number(plain[2]);
  return 99999;
}

const page = { display: "grid", gap: "18px" };
const pageHeader = { display: "flex", justifyContent: "space-between", alignItems: "flex-start" };
const headerActions = { display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" };
const title = { margin: 0, fontSize: "18px", fontWeight: 800 };
const subtitle = { margin: "6px 0 0", color: "#64748b", fontSize: "12px" };
const dateWrap = { display: "flex", alignItems: "center", gap: "10px", background: "#fff", border: "1px solid #cbd5e1", height: "52px", padding: "0 14px", borderRadius: "14px" };
const dateInput = { border: 0, outline: 0, fontSize: "16px", fontWeight: 700, background: "transparent" };
const refreshButton = { height: "52px", padding: "0 16px", border: "1px solid #bfdbfe", background: "#eff6ff", color: "#2563eb", borderRadius: "14px", fontWeight: 800, display: "inline-flex", alignItems: "center", gap: "8px", cursor: "pointer" };
const summaryCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "22px", padding: "16px", boxShadow: "0 8px 24px rgba(15,23,42,0.05)" };
const summaryGrid = { display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: "12px" };
const summaryBox = { minHeight: "92px", borderRadius: "18px", padding: "14px 18px", display: "grid", alignContent: "center", gap: "6px" };
const summaryValue = { fontSize: "20px", fontWeight: 800, color: "#0f172a" };
const summaryLabel = { fontSize: "11px", fontWeight: 700, color: "#64748b" };
const marketGrid = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "16px" };
const marketCard = { border: "1px solid #dbe4f0", borderRadius: "20px", background: "#fff", overflow: "hidden", textAlign: "left", cursor: "pointer", boxShadow: "0 10px 24px rgba(15,23,42,0.05)" };
const marketHeader = { minHeight: "74px", color: "#fff", padding: "16px", display: "flex", alignItems: "flex-start", justifyContent: "space-between" };
const marketActionBar = { padding: "10px 12px 0" };
const deleteButton = { height: "34px", padding: "0 12px", border: 0, borderRadius: "10px", background: "#fee2e2", color: "#dc2626", fontWeight: 800, cursor: "pointer" };
const marketTitleRow = { display: "flex", alignItems: "center", gap: "12px", fontSize: "15px", fontWeight: 800 };
const marketInitial = { width: "42px", height: "42px", display: "grid", placeItems: "center", background: "rgba(255,255,255,0.2)", fontWeight: 800 };
const marketTotal = { textAlign: "right", display: "grid", gap: "4px", fontSize: "12px" };
const sessionPanelOpen = { margin: "12px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "0", padding: "14px 14px 16px", display: "flex", justifyContent: "space-between", color: "#166534" };
const sessionPanelClose = { margin: "0 12px 12px", background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: "0", padding: "14px 14px 16px", display: "flex", justifyContent: "space-between", color: "#b91c1c" };
const sessionMeta = { fontSize: "12px", color: "#64748b", marginTop: "8px" };
const sessionValueGreen = { marginTop: "4px", fontSize: "16px", fontWeight: 800, color: "#15803d" };
const sessionValueRed = { marginTop: "4px", fontSize: "16px", fontWeight: 800, color: "#dc2626" };
const betsMeta = { display: "grid", alignContent: "end", justifyItems: "end", gap: "6px", fontSize: "12px" };
const cardFoot = { minHeight: "44px", borderTop: "1px solid #eef2f7", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px", color: "#475569", fontSize: "12px", fontWeight: 700 };
const cardFootText = { color: "#64748b" };
const cardFootLink = { color: "#2563eb", fontWeight: 800 };
const emptyState = { minHeight: "160px", borderRadius: "22px", background: "#fff", border: "1px solid #e2e8f0", display: "grid", placeItems: "center", textAlign: "center", color: "#64748b", gap: "6px", padding: "24px" };
export default AdminStakeAnalysis;

function getIndiaDateValue() {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return formatter.format(new Date());
}
